/**
 * Colour thumbnails on the product page (snippets/pdp-farbauswahl.liquid).
 * The server already links every colour to its variant in the selected
 * size; this re-links them when the shopper picks another size, so the
 * size travels along to the other colour. Each link carries its product's
 * variants (`data-variants`: id, size value, availability) and its plain
 * URL (`data-url`). Nothing to do for products without a size option.
 * On phones the thumbnails are one row that scrolls sideways; the current
 * colour is scrolled into view on load. Hovering or focusing a colour shows
 * its name (with "ausverkauft" when sold out) in the header until the
 * pointer or focus leaves the row.
 */
if (!customElements.get('pdp-farbauswahl')) {
  customElements.define('pdp-farbauswahl', class extends HTMLElement {
    connectedCallback() {
      this.revealCurrent();
      this.previewNames();
      this.sizeIndex = Number(this.dataset.sizeIndex);
      if (!(this.sizeIndex >= 0) || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId === this.dataset.sectionId) this.relink(data.variant);
      });
    }

    disconnectedCallback() {
      this.unsubscribeVariant?.();
    }

    /** Centres the current colour in the row when the row scrolls, without moving the page. */
    revealCurrent() {
      const list = this.querySelector('.pdp-colours__list');
      const current = list?.querySelector('[aria-current]');
      if (!current || list.scrollWidth <= list.clientWidth) return;
      const offset = current.getBoundingClientRect().left - list.getBoundingClientRect().left;
      list.scrollLeft += offset - (list.clientWidth - current.offsetWidth) / 2;
    }

    /** Swaps the header's colour name for the hovered or focused one. */
    previewNames() {
      const name = this.querySelector('[data-colour-current]');
      if (!name) return;
      const current = name.textContent;
      const show = (event) => {
        const link = event.target.closest?.('a[aria-label]');
        name.textContent = link ? link.getAttribute('aria-label') : current;
      };
      this.addEventListener('mouseover', show);
      this.addEventListener('focusin', show);
      this.addEventListener('mouseleave', () => { name.textContent = current; });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) name.textContent = current;
      });
    }

    /** Points every colour at its available variant in `variant`'s size, else at the product. */
    relink(variant) {
      const size = variant?.options?.[this.sizeIndex];
      for (const link of this.querySelectorAll('a[data-variants]:not([aria-current])')) {
        let match = null;
        if (size != null) {
          try {
            match = JSON.parse(link.dataset.variants).find((candidate) => candidate.available && candidate.size === size);
          } catch { match = null; }
        }
        link.setAttribute('href', match ? `${link.dataset.url}?variant=${match.id}` : link.dataset.url);
      }
    }
  });
}
